import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Calendar, Clock, MapPin, Music2, Ticket, Radio, Eye, Heart, MessageCircle, ExternalLink, Users, Share2 } from 'lucide-react';
import { getEventTypeColor, getEventTypeIcon } from '../eventHelpers';
import { renderLiveIndicator, renderOverBadge } from '../eventComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EventDetailModal = ({ 
  show, 
  onClose, 
  selectedEvent, 
  renderReactionButtons,
  onOpenPromo,
  onViewInMyEvents,
  onDiscordShare
}) => {
  const [linkedPromos, setLinkedPromos] = useState([]);
  const [loadingPromos, setLoadingPromos] = useState(false);

  useEffect(() => {
    if (show && selectedEvent) {
      fetchLinkedPromos();
    }
  }, [show, selectedEvent]);

  const fetchLinkedPromos = async () => {
    setLoadingPromos(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/promos?category=event&limit=50`);
      const data = await response.json();
      
      if (data.success) {
        // Filter promos linked to this event
        const linked = data.promos.filter(promo => promo.linked_event_id === selectedEvent.id);
        setLinkedPromos(linked);
      }
    } catch (error) {
      console.error('Failed to fetch linked promos:', error);
    } finally {
      setLoadingPromos(false);
    }
  };

  if (!show) return null;
  
  return ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10003,
            padding: '1rem'
          }}
          onClick={() => onClose()}
        >
          <div
            style={{
              width: '90%',
              maxWidth: '800px',
              maxHeight: '90vh',
              background: 'rgba(26, 26, 46, 0.98)',
              borderRadius: '16px',
              overflow: 'hidden',
              border: selectedEvent.is_live ? '3px solid transparent' : `3px solid ${getEventTypeColor(selectedEvent.event_type)}`,
              backgroundImage: selectedEvent.is_live 
                ? 'linear-gradient(rgba(26, 26, 46, 0.98), rgba(26, 26, 46, 0.98)), linear-gradient(90deg, #00ff00, #00ffff, #00ff00, #00ffff)'
                : 'none',
              backgroundOrigin: 'border-box',
              backgroundClip: selectedEvent.is_live ? 'padding-box, border-box' : 'border-box',
              animation: selectedEvent.is_live ? 'gradientBorder 3s linear infinite' : 'none',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {selectedEvent.is_live && (
              <style>{`
                @keyframes gradientBorder {
                  0% {
                    background-image: 
                      linear-gradient(rgba(26, 26, 46, 0.98), rgba(26, 26, 46, 0.98)),
                      linear-gradient(90deg, #00ff00, #00ffff, #00ff00, #00ffff);
                  }
                  25% {
                    background-image: 
                      linear-gradient(rgba(26, 26, 46, 0.98), rgba(26, 26, 46, 0.98)),
                      linear-gradient(180deg, #00ffff, #00ff00, #00ffff, #00ff00);
                  }
                  50% {
                    background-image: 
                      linear-gradient(rgba(26, 26, 46, 0.98), rgba(26, 26, 46, 0.98)),
                      linear-gradient(270deg, #00ff00, #00ffff, #00ff00, #00ffff);
                  }
                  75% {
                    background-image: 
                      linear-gradient(rgba(26, 26, 46, 0.98), rgba(26, 26, 46, 0.98)),
                      linear-gradient(360deg, #00ffff, #00ff00, #00ffff, #00ff00);
                  }
                  100% {
                    background-image: 
                      linear-gradient(rgba(26, 26, 46, 0.98), rgba(26, 26, 46, 0.98)),
                      linear-gradient(90deg, #00ff00, #00ffff, #00ff00, #00ffff);
                  }
                }
              `}</style>
            )}
            {/* Modal Header */}
            <div style={{
              padding: '2rem',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexShrink: 0
            }}>
              <div style={{ flex: 1 }}>
                <h2 style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: '#fff',
                  margin: '0 0 0.5rem 0'
                }}>
                  {getEventTypeIcon(selectedEvent.event_type)} {selectedEvent.event_name}
                </h2>
                <p style={{
                  fontSize: '1.25rem',
                  color: getEventTypeColor(selectedEvent.event_type),
                  fontWeight: '600',
                  margin: '0.5rem 0'
                }}>
                  {selectedEvent.venue_name}
                </p>
                <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  {selectedEvent.is_live ? renderLiveIndicator(selectedEvent, 'small') : selectedEvent.is_over ? renderOverBadge(selectedEvent, 'small', 'red') : null}
                  <span style={{ color: 'rgba(255, 255, 255, 0.7)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                    <Calendar size={16} /> {new Date(selectedEvent.event_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  <span style={{ color: 'rgba(255, 255, 255, 0.7)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                    <Clock size={16} /> {selectedEvent.event_time}
                  </span>
                  <span style={{ color: 'rgba(255, 255, 255, 0.7)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                    <MapPin size={16} /> {selectedEvent.location}
                  </span>
                  <span style={{ color: 'rgba(255, 255, 255, 0.7)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                    <Music2 size={16} /> {selectedEvent.genre}
                  </span>
                </div>
                
                {/* Social Actions Row - Attendance + Reactions + Discord */}
                <div style={{ 
                  marginTop: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  flexWrap: 'wrap'
                }}>
                  {/* Attendance Count */}
                  <span style={{
                    padding: '0.4rem 0.8rem',
                    background: 'rgba(0, 255, 255, 0.1)',
                    border: '1px solid rgba(0, 255, 255, 0.3)',
                    borderRadius: '16px',
                    fontSize: '0.85rem',
                    fontWeight: '500',
                    color: '#00ffff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem'
                  }}>
                    <Users size={14} />
                    {selectedEvent.attendance_count || 0} Going
                  </span>
                  
                  {/* Reaction Buttons */}
                  {renderReactionButtons(selectedEvent, 'large')}
                  
                  {/* Discord Share Icon */}
                  <button
                    onClick={() => {
                      if (onDiscordShare) {
                        onDiscordShare({
                          title: selectedEvent.event_name,
                          artist: selectedEvent.venue_name,
                          artwork: selectedEvent.image_url || '',
                          genre: selectedEvent.genre,
                          location: selectedEvent.location,
                          date: selectedEvent.event_date,
                          url: selectedEvent.ticket_url || ''
                        });
                      } else {
                        alert('Please connect your Discord account first');
                      }
                    }}
                    style={{
                      background: 'rgba(88, 101, 242, 0.15)',
                      border: '1px solid rgba(88, 101, 242, 0.3)',
                      borderRadius: '8px',
                      padding: '0.4rem 0.6rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(88, 101, 242, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(88, 101, 242, 0.15)';
                    }}
                    title="Share to Discord"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#7289da">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                    </svg>
                  </button>
                </div>
                
                {/* Event Source + View in My Events - Simple text row */}
                <div style={{ 
                  marginTop: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1.5rem',
                  fontSize: '0.85rem'
                }}>
                  {/* Event Source - Simple text */}
                  <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                    Source: {' '}
                    <span style={{ color: '#00ffff' }}>
                      {selectedEvent.source === 'skiddle' || selectedEvent.event_source === 'skiddle' ? '🎭 Skiddle' : 
                       selectedEvent.source === 'ticketmaster' || selectedEvent.event_source === 'ticketmaster' ? '🎫 Ticketmaster' : 
                       selectedEvent.source === 'edmtrain' || selectedEvent.event_source === 'edmtrain' ? '🎵 Edmtrain' : 
                       selectedEvent.source === 'eventbrite' || selectedEvent.event_source === 'eventbrite' ? '🎟️ Eventbrite' :
                       selectedEvent.user_name ? `🎧 ${selectedEvent.user_name}` : '🎧 BeatSeek'}
                    </span>
                  </span>
                  
                  {/* View in My Events - Simple text link using onViewInMyEvents prop */}
                  <button
                    onClick={() => {
                      // Close the modal and use the proper callback
                      onClose();
                      if (onViewInMyEvents) {
                        onViewInMyEvents();
                      }
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#00ffff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      padding: 0,
                      fontSize: '0.85rem',
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#ff0080'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#00ffff'}
                  >
                    <ExternalLink size={14} /> View in My Events
                  </button>
                </div>
              </div>

              <button
                onClick={() => onClose()}
                style={{
                  background: 'rgba(255, 0, 128, 0.2)',
                  border: '1px solid rgba(255, 0, 128, 0.4)',
                  color: '#ff0080',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.25rem',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Content */}
            <div 
              className="event-detail-scrollable"
              style={{ 
                padding: '2rem', 
                overflowY: 'auto', 
                flex: 1,
                WebkitOverflowScrolling: 'touch'
              }}
            >
              <style>{`
                .event-detail-scrollable::-webkit-scrollbar {
                  width: 8px;
                }
                .event-detail-scrollable::-webkit-scrollbar-track {
                  background: rgba(0, 0, 0, 0.2);
                  border-radius: 4px;
                }
                .event-detail-scrollable::-webkit-scrollbar-thumb {
                  background: rgba(0, 255, 255, 0.3);
                  border-radius: 4px;
                }
                .event-detail-scrollable::-webkit-scrollbar-thumb:hover {
                  background: rgba(0, 255, 255, 0.5);
                }
              `}</style>

              {/* Description */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  color: '#fff',
                  marginBottom: '1rem'
                }}>
                  About This Event
                </h3>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  lineHeight: '1.6',
                  fontSize: '1rem',
                  margin: 0
                }}>
                  {selectedEvent.description}
                </p>
              </div>

              {/* Links Section */}
              {(selectedEvent.ticket_url || selectedEvent.stream_url) && (
                <div style={{
                  padding: '1.5rem',
                  background: 'rgba(0, 255, 255, 0.1)',
                  borderRadius: '12px',
                  border: '1px solid rgba(0, 255, 255, 0.3)',
                  marginBottom: '2rem'
                }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    color: '#fff',
                    marginBottom: '1rem'
                  }}>
                    Event Links
                  </h3>
                  {selectedEvent.ticket_url && (
                    <a
                      href={selectedEvent.ticket_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1.5rem',
                        background: 'rgba(255, 0, 255, 0.2)',
                        border: '1px solid rgba(255, 0, 255, 0.4)',
                        borderRadius: '8px',
                        color: '#ff00ff',
                        textDecoration: 'none',
                        fontWeight: '600',
                        marginRight: '1rem',
                        marginBottom: '0.5rem',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 0, 255, 0.3)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 0, 255, 0.2)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <Ticket size={18} /> Tickets/Info
                    </a>
                  )}
                  {selectedEvent.stream_url && (
                    <a
                      href={selectedEvent.stream_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1.5rem',
                        background: 'rgba(255, 0, 100, 0.2)',
                        border: '1px solid rgba(255, 0, 100, 0.4)',
                        borderRadius: '8px',
                        color: '#ff0064',
                        textDecoration: 'none',
                        fontWeight: '600',
                        marginBottom: '0.5rem',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 0, 100, 0.3)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 0, 100, 0.2)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <Radio size={18} /> Watch Stream
                    </a>
                  )}
                </div>
              )}

              {/* Linked Promos Section */}
              <div style={{
                padding: '1.5rem',
                background: 'rgba(255, 215, 0, 0.1)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 215, 0, 0.3)'
              }}>
                <h3 style={{
                  fontSize: '0.625rem',
                  fontWeight: '700',
                  color: '#ffd700',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  📢 Linked Promos ({linkedPromos.length})
                </h3>
                
                {loadingPromos ? (
                  <p style={{ color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
                    Loading promos...
                  </p>
                ) : linkedPromos.length === 0 ? (
                  <p style={{ color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
                    No promos linked to this event yet.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {linkedPromos.map(promo => (
                      <div
                        key={promo.id}
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '10px',
                          padding: '1rem',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          cursor: 'pointer',
                          transition: 'all 0.3s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                          e.currentTarget.style.borderColor = 'rgba(255, 51, 102, 0.5)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        }}
                        onClick={() => {
                          if (onOpenPromo) {
                            onOpenPromo(promo);
                          }
                        }}
                      >
                        {/* Promo Header */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #ff0066, #ff3366)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: '700',
                            fontSize: '14px',
                            overflow: 'hidden'
                          }}>
                            {promo.user_avatar ? (
                              <img 
                                src={`${BACKEND_URL}${promo.user_avatar}`} 
                                alt={promo.user_name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              promo.user_name.charAt(0).toUpperCase()
                            )}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: 'white', fontWeight: '600', fontSize: '14px' }}>
                              {promo.user_name}
                            </div>
                            <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}>
                              {new Date(promo.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          {promo.is_boosted && (
                            <div style={{
                              background: 'linear-gradient(135deg, #ffd700, #ffed4e)',
                              color: '#1a1a2e',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '700'
                            }}>
                              ⭐
                            </div>
                          )}
                        </div>

                        {/* Promo Content */}
                        {promo.title && (
                          <h4 style={{
                            color: 'white',
                            fontSize: '16px',
                            fontWeight: '600',
                            margin: '0 0 0.5rem 0'
                          }}>
                            {promo.title}
                          </h4>
                        )}
                        {promo.description && (
                          <p style={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: '14px',
                            margin: '0 0 0.75rem 0',
                            lineHeight: '1.4'
                          }}>
                            {promo.description.length > 100 
                              ? `${promo.description.substring(0, 100)}...` 
                              : promo.description}
                          </p>
                        )}

                        {/* Promo Engagement */}
                        <div style={{
                          display: 'flex',
                          gap: '1rem',
                          fontSize: '12px',
                          color: 'rgba(255, 255, 255, 0.6)'
                        }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Heart size={14} /> {promo.likes_count || 0}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <MessageCircle size={14} /> {promo.comments_count || 0}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Eye size={14} /> {promo.views_count || 0}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
    document.body
  );
};

export default EventDetailModal;
